// Narrow, non-blocking Stage 5 review receipt shared by the browser-side
// completion callers. PostgreSQL repeats this validation at the trust boundary;
// this copy prevents internal callers from putting unrelated project data on
// the completion wire and gives the App a deterministic verification shape.

const MAX_REVIEW_FINDINGS = 50;
const MAX_REVIEW_TEXT_LENGTH = 2000;
const MAX_REVIEW_RECEIPT_BYTES = 64 * 1024;

export interface Stage5ReadinessReviewCounts {
    pricingBlockers: number;
    evidenceBlockers: number;
    evidenceWarnings: number;
}

export interface Stage5ReadinessReviewPricing {
    quoteRevenueExGst: number;
    knownCostExGst: number;
    shortfall: number;
    pendingInclusionPricingReviewCount: number;
    overAllocatedAllowanceParentCount: number;
}

export interface Stage5ReadinessReviewEvidence {
    projectPhotoCount: number;
    assignedPhotoCount: number;
    needsPhotoAssignment: boolean;
}

export interface Stage5ReadinessReview {
    code: 'QUOTE_REVIEW_REQUIRED';
    needsReview: true;
    blocking: false;
    assessedAt: string;
    reviewStage: 5;
    counts: Stage5ReadinessReviewCounts;
    issues: string[];
    warnings: string[];
    pricing: Stage5ReadinessReviewPricing;
    evidence: Stage5ReadinessReviewEvidence;
}

interface CustomError extends TypeError {
    code?: string;
}

function invalidReadinessReview(message: string): CustomError {
    const error: CustomError = new TypeError(message);
    error.code = 'INVALID_READINESS_REVIEW';
    return error;
}

function record(value: unknown, field: string): Record<string, any> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        throw invalidReadinessReview(`${field} must be an object.`);
    }
    return value as Record<string, any>;
}

function nonNegativeFinite(value: unknown, field: string): number {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
        throw invalidReadinessReview(`${field} must be a non-negative finite number.`);
    }
    return value;
}

function nonNegativeInteger(value: unknown, field: string): number {
    const number = nonNegativeFinite(value, field);
    if (!Number.isSafeInteger(number)) {
        throw invalidReadinessReview(`${field} must be a non-negative safe integer.`);
    }
    return number;
}

function reviewMessages(value: unknown, field: string): string[] {
    if (!Array.isArray(value) || value.length > MAX_REVIEW_FINDINGS) {
        throw invalidReadinessReview(`${field} must contain at most ${MAX_REVIEW_FINDINGS} messages.`);
    }
    return value.map((message, index) => {
        if (typeof message !== 'string') {
            throw invalidReadinessReview(`${field}[${index}] must be text.`);
        }
        const trimmed = message.trim();
        if (!trimmed || trimmed.length > MAX_REVIEW_TEXT_LENGTH) {
            throw invalidReadinessReview(`${field}[${index}] must contain 1-${MAX_REVIEW_TEXT_LENGTH} characters.`);
        }
        return trimmed;
    });
}

export function sanitizeStage5ReadinessReview(value: unknown): Stage5ReadinessReview | null {
    if (value == null) return null;
    const source = record(value, 'readinessReview');
    if (source.code !== 'QUOTE_REVIEW_REQUIRED'
        || source.needsReview !== true
        || source.blocking !== false
        || source.reviewStage !== 5) {
        throw invalidReadinessReview(
            'readinessReview must be a non-blocking Stage 5 QUOTE_REVIEW_REQUIRED receipt.',
        );
    }
    if (typeof source.assessedAt !== 'string' || !Number.isFinite(Date.parse(source.assessedAt))) {
        throw invalidReadinessReview('readinessReview.assessedAt must be a valid timestamp.');
    }

    const countsSource = record(source.counts, 'readinessReview.counts');
    const issues = reviewMessages(source.issues, 'readinessReview.issues');
    const warnings = reviewMessages(source.warnings, 'readinessReview.warnings');
    const counts = {
        pricingBlockers: nonNegativeInteger(countsSource.pricingBlockers, 'readinessReview.counts.pricingBlockers'),
        evidenceBlockers: nonNegativeInteger(countsSource.evidenceBlockers, 'readinessReview.counts.evidenceBlockers'),
        evidenceWarnings: nonNegativeInteger(countsSource.evidenceWarnings, 'readinessReview.counts.evidenceWarnings'),
    };
    if (counts.pricingBlockers + counts.evidenceBlockers !== issues.length
        || counts.evidenceWarnings !== warnings.length) {
        throw invalidReadinessReview('readinessReview counts do not match its issue and warning messages.');
    }
    if (issues.length + warnings.length === 0) {
        throw invalidReadinessReview('readinessReview must contain at least one issue or warning.');
    }

    const pricingSource = record(source.pricing, 'readinessReview.pricing');
    const evidenceSource = record(source.evidence, 'readinessReview.evidence');
    const review: Stage5ReadinessReview = {
        code: 'QUOTE_REVIEW_REQUIRED',
        needsReview: true,
        blocking: false,
        assessedAt: new Date(source.assessedAt).toISOString(),
        reviewStage: 5,
        counts,
        issues,
        warnings,
        pricing: {
            quoteRevenueExGst: nonNegativeFinite(pricingSource.quoteRevenueExGst, 'readinessReview.pricing.quoteRevenueExGst'),
            knownCostExGst: nonNegativeFinite(pricingSource.knownCostExGst, 'readinessReview.pricing.knownCostExGst'),
            shortfall: nonNegativeFinite(pricingSource.shortfall, 'readinessReview.pricing.shortfall'),
            pendingInclusionPricingReviewCount: nonNegativeInteger(
                pricingSource.pendingInclusionPricingReviewCount,
                'readinessReview.pricing.pendingInclusionPricingReviewCount',
            ),
            overAllocatedAllowanceParentCount: nonNegativeInteger(
                pricingSource.overAllocatedAllowanceParentCount,
                'readinessReview.pricing.overAllocatedAllowanceParentCount',
            ),
        },
        evidence: {
            projectPhotoCount: nonNegativeInteger(evidenceSource.projectPhotoCount, 'readinessReview.evidence.projectPhotoCount'),
            assignedPhotoCount: nonNegativeInteger(evidenceSource.assignedPhotoCount, 'readinessReview.evidence.assignedPhotoCount'),
            needsPhotoAssignment: evidenceSource.needsPhotoAssignment === true,
        },
    };
    if (typeof evidenceSource.needsPhotoAssignment !== 'boolean') {
        throw invalidReadinessReview('readinessReview.evidence.needsPhotoAssignment must be boolean.');
    }
    if (JSON.stringify(review).length > MAX_REVIEW_RECEIPT_BYTES) {
        throw invalidReadinessReview('readinessReview exceeds the 64 KiB completion receipt limit.');
    }
    return review;
}

export function stage5ReadinessReviewsMatch(expected: unknown, actual: unknown): boolean {
    try {
        return JSON.stringify(sanitizeStage5ReadinessReview(expected))
            === JSON.stringify(sanitizeStage5ReadinessReview(actual));
    } catch {
        return false;
    }
}
